/**
 * @file schemas.ts
 * @brief Zod schemas for Company Admin Reports API responses.
 *
 * @references
 * - BACKEND/src/features/company/reports.service.js
 *
 * @author DES
 * @date 27-07-2026
 */

import { z } from "zod";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
  z.null().transform(() => 0),
]);

const nullableNumber = z
  .union([z.number(), z.string(), z.null()])
  .transform((v) => {
    if (v == null || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  });

export type CompanyReportRange = {
  startDate?: string;
  endDate?: string;
};

export const UsageByEmployeeRowSchema = z
  .object({
    user_id: z.string(),
    email: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
    interpretation_sessions: numericString,
    interpretation_seconds: numericString,
    interpretation_cost_cents: numericString,
    meeting_count: numericString,
    meeting_seconds: numericString,
    meeting_cost_cents: numericString,
    note_count: numericString,
    note_seconds: numericString,
    note_cost_cents: numericString,
    // (24-08-2026) The three spend columns are added together in the UI, so the
    // row carries the currency they are all in. Reports used to return none and
    // every component defaulted to a hard-coded "GBP".
    currency: z.string().nullable().optional(),
  })
  .passthrough();
export const UsageByEmployeeSchema = z.array(UsageByEmployeeRowSchema);
export type UsageByEmployeeRow = z.infer<typeof UsageByEmployeeRowSchema>;

// (24-08-2026) `cost_cents` (Verblizr's AI vendor COGS) and `profit_cents` were
// in this payload and rendered on a CUSTOMER's page — see the rewrite note on
// BACKEND getSpendByProductReport. The endpoint no longer returns either, and
// the schema no longer accepts them, so a regression fails validation loudly
// instead of quietly re-exposing margin. Rows are per (product, currency).
export const SpendByProductRowSchema = z
  .object({
    product: z.string(),
    product_label: z.string(),
    currency: z.string().nullable().optional(),
    invoice_count: numericString,
    spend_cents: numericString,
  })
  .strict();
export const SpendByProductSchema = z.array(SpendByProductRowSchema);
export type SpendByProductRow = z.infer<typeof SpendByProductRowSchema>;

export const MeetingIntelReportSchema = z
  .object({
    summary: z
      .object({
        meeting_count: numericString,
        total_duration_seconds: numericString,
        avg_engagement: nullableNumber,
        avg_sentiment: nullableNumber,
        avg_read_score: nullableNumber,
        total_action_items: numericString,
        total_decisions: numericString,
      })
      .passthrough(),
    by_status: z.array(
      z
        .object({
          status: z.string().nullable(),
          count: numericString,
        })
        .passthrough(),
    ),
    meetings: z.array(
      z
        .object({
          id: z.string(),
          title: z.string().nullable().optional(),
          started_at: z.string().nullable().optional(),
          status: z.string().nullable().optional(),
          total_duration_seconds: numericString,
          engagement_score: nullableNumber,
          sentiment_score: nullableNumber,
          read_score: nullableNumber,
          action_item_count: numericString,
          owner_email: z.string().nullable().optional(),
          owner_first_name: z.string().nullable().optional(),
          owner_last_name: z.string().nullable().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type MeetingIntelReport = z.infer<typeof MeetingIntelReportSchema>;

export const ProductMixRowSchema = z
  .object({
    product: z.string(),
    product_label: z.string(),
    currency: z.string().nullable().optional(),
    item_count: numericString,
    total_seconds: numericString,
    spend_cents: numericString,
    item_share_pct: numericString,
    time_share_pct: numericString,
    spend_share_pct: numericString,
  })
  .passthrough();
export const ProductMixSchema = z.array(ProductMixRowSchema);
export type ProductMixRow = z.infer<typeof ProductMixRowSchema>;

export const ScheduleOpsReportSchema = z
  .object({
    summary: z
      .object({
        total_scheduled: numericString,
        by_status: z.record(z.string(), numericString).optional(),
        reschedule_by_status: z.record(z.string(), numericString).optional(),
        reschedule_approve_rate_pct: nullableNumber,
      })
      .passthrough(),
  })
  .passthrough();
export type ScheduleOpsReport = z.infer<typeof ScheduleOpsReportSchema>;

export const EnrollmentReportSchema = z
  .object({
    summary: z
      .object({
        total: numericString,
        enrolled: numericString,
        not_enrolled: numericString,
        enrolled_pct: numericString,
      })
      .passthrough(),
    employees: z.array(
      z
        .object({
          user_id: z.string(),
          email: z.string().nullable().optional(),
          first_name: z.string().nullable().optional(),
          last_name: z.string().nullable().optional(),
          status: z.string().nullable().optional(),
          role: z.string().nullable().optional(),
          voice_enrollment_status: z.string().nullable().optional(),
          enrolled: z.boolean(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type EnrollmentReport = z.infer<typeof EnrollmentReportSchema>;

export const AdoptionReportSchema = z
  .object({
    feature_access: z
      .object({
        interpretation: z.boolean(),
        notes: z.boolean(),
        boardroom: z.boolean(),
        medical_assessment: z.boolean(),
      })
      .passthrough(),
    summary: z
      .object({
        employees: numericString,
        regular_employees: numericString,
        idle_regular: numericString,
        active_regular: numericString,
        used_interpretation: numericString,
        used_boardroom: numericString,
        used_notes: numericString,
      })
      .passthrough(),
    employees: z.array(
      z
        .object({
          user_id: z.string(),
          email: z.string().nullable().optional(),
          first_name: z.string().nullable().optional(),
          last_name: z.string().nullable().optional(),
          status: z.string().nullable().optional(),
          role: z.string().nullable().optional(),
          interpretation_sessions: numericString,
          meeting_count: numericString,
          note_count: numericString,
          active_products: z.array(z.string()),
          idle: z.boolean(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
export type AdoptionReport = z.infer<typeof AdoptionReportSchema>;
